import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Edit3,
    Trash2,
    Eye,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    Check,
    PlusCircle,
    Layers,
    ShieldCheck,
    Calendar,
    FileText
} from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { api } from "../../services/api";
import { BlogPost } from "../../global-components/src/types";
import { getImageUser, getImageUserNotFound, hasPosition } from "../../global-components/src/utils/auth";
import { BUCKET_PACOCA_URL } from "../../global-components/src/utils/vars";
import ImgVerified from "../../global-components/src/components/img-verified";
import BlogNavbar from "./components/BlogNavbar";
import "./blog-manage.css";

const coverSrc = (v: string) => v.startsWith("http") ? v : `${BUCKET_PACOCA_URL}/${v}`;

const BlogManage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get("tab") === "moderation" ? "moderation" : "my-posts";

    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState<"my-posts" | "moderation">(initialTab);
    const [statusFilter, setStatusFilter] = useState<string>("all");

    const [myPosts, setMyPosts] = useState<BlogPost[]>([]);
    const [pendingPosts, setPendingPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const canModerate = Boolean(
        hasPosition(user) || user?.permissions?.includes("approve_blog_post")
    );

    useEffect(() => {
        if (!token) {
            navigate("/login");
            return;
        }

        if (activeTab === "my-posts") {
            fetchMyPosts();
        } else if (activeTab === "moderation") {
            if (!canModerate) {
                setActiveTab("my-posts");
            } else {
                fetchPendingPosts();
            }
        }
    }, [activeTab, statusFilter, token]);

    const fetchMyPosts = async () => {
        try {
            setLoading(true);
            const query = statusFilter !== "all" ? `?status=${statusFilter}` : "";
            const res = await api(token).get(`/blog/my-posts${query}`);
            setMyPosts(res.data?.data || []);
        } catch (err: any) {
            setMessage(t("blog.manage.loadError"), "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchPendingPosts = async () => {
        try {
            setLoading(true);
            const res = await api(token).get(`/blog/pending`);
            setPendingPosts(res.data?.data || []);
        } catch (err: any) {
            setMessage(t("blog.manage.loadModError"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (postId: number) => {
        try {
            setActionLoading(true);
            await api(token).post(`/blog/${postId}/approve`);
            setMessage(t("blog.approveSuccess"), "success");
            fetchPendingPosts();
        } catch (err: any) {
            setMessage(t("blog.approveError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (postId: number) => {
        const reason = window.prompt(t("blog.rejectReasonPrompt"), t("blog.defaultRejectReason"));
        if (reason === null) return;

        try {
            setActionLoading(true);
            await api(token).post(`/blog/${postId}/reject`, { reason });
            setMessage(t("blog.rejectSuccess"), "warning");
            fetchPendingPosts();
        } catch (err: any) {
            setMessage(t("blog.rejectError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (postId: number) => {
        if (!window.confirm(t("blog.confirmDelete"))) return;

        try {
            setActionLoading(true);
            await api(token).delete(`/blog/${postId}`);
            setMessage(t("blog.deleteSuccess"), "success");
            if (activeTab === "my-posts") {
                fetchMyPosts();
            } else {
                fetchPendingPosts();
            }
        } catch (err: any) {
            setMessage(t("blog.deleteError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "published":
                return <span className="status-badge badge-published"><Check size={12} /> {t("blog.status.published")}</span>;
            case "pending":
                return <span className="status-badge badge-pending"><Clock size={12} /> {t("blog.status.pending")}</span>;
            case "draft":
                return <span className="status-badge badge-draft"><FileText size={12} /> {t("blog.status.draft")}</span>;
            case "rejected":
                return <span className="status-badge badge-rejected"><AlertCircle size={12} /> {t("blog.status.rejected")}</span>;
            default:
                return null;
        }
    };

    return (
        <div className="blog-manage-page">
            <Helmet>
                <title>{t("blog.manage.title")} – {t("blog.title")}</title>
            </Helmet>

            <BlogNavbar />

            <div className="blog-manage-hero">
                <div className="blog-manage-hero-inner">
                    <div className="blog-manage-hero-header">
                        <div>
                            <h1 className="blog-manage-title">{t("blog.manage.title")}</h1>
                            <p className="blog-manage-subtitle">{t("blog.manage.subtitle")}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate("/blog/escrever")}
                            className="blog-write-cta-btn"
                        >
                            <PlusCircle size={16} />
                            <span>{t("blog.manage.createNew")}</span>
                        </button>
                    </div>

                    <div className="blog-manage-tabs">
                        <button
                            type="button"
                            className={`blog-manage-tab ${activeTab === "my-posts" ? "active" : ""}`}
                            onClick={() => {
                                setActiveTab("my-posts");
                                setSearchParams({ tab: "my-posts" });
                            }}
                        >
                            <Layers size={16} />
                            <span>{t("blog.manage.myPostsTab")}</span>
                        </button>

                        {canModerate && (
                            <button
                                type="button"
                                className={`blog-manage-tab ${activeTab === "moderation" ? "active" : ""}`}
                                onClick={() => {
                                    setActiveTab("moderation");
                                    setSearchParams({ tab: "moderation" });
                                }}
                            >
                                <ShieldCheck size={16} />
                                <span>{t("blog.manage.moderationTab", { count: pendingPosts.length })}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <main className="blog-manage-main">
                <div className="blog-manage-container">
                    {activeTab === "my-posts" && (
                        <>
                            {/* Filter by status */}
                            <div className="blog-status-filter-pills">
                                {[
                                    { key: "all", label: t("blog.status.all") },
                                    { key: "published", label: t("blog.status.published") },
                                    { key: "pending", label: t("blog.status.pending") },
                                    { key: "draft", label: t("blog.status.draft") },
                                    { key: "rejected", label: t("blog.status.rejected") },
                                ].map((pill) => (
                                    <button
                                        key={pill.key}
                                        type="button"
                                        className={`filter-pill ${statusFilter === pill.key ? "active" : ""}`}
                                        onClick={() => setStatusFilter(pill.key)}
                                    >
                                        {pill.label}
                                    </button>
                                ))}
                            </div>

                            {loading ? (
                                <div className="blog-manage-loading">{t("blog.loading")}</div>
                            ) : myPosts.length === 0 ? (
                                <div className="blog-manage-empty">
                                    <FileText size={48} className="empty-icon" />
                                    <h3>{t("blog.manage.emptyMyPosts")}</h3>
                                    <p>{t("blog.manage.emptyMyPostsDesc")}</p>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/blog/escrever")}
                                        className="blog-write-cta-btn mt-3"
                                    >
                                        <PlusCircle size={16} />
                                        <span>{t("blog.createFirst")}</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="blog-manage-list">
                                    {myPosts.map((post) => (
                                        <div key={post.id} className="blog-manage-card">
                                            {post.cover_image && (
                                                <div className="manage-card-img-wrapper">
                                                    <img src={coverSrc(post.cover_image!)} alt={post.title} className="manage-card-img" />
                                                </div>
                                            )}
                                            <div className="manage-card-body">
                                                <div className="manage-card-header">
                                                    {getStatusBadge(post.status)}
                                                    <span className="manage-card-date">
                                                        <Calendar size={12} />
                                                        {new Date(post.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" })}
                                                    </span>
                                                    <span className="manage-card-readtime">
                                                        <Clock size={12} />
                                                        {t("blog.readTimeShort", { count: post.read_time })}
                                                    </span>
                                                </div>

                                                <h3 className="manage-card-title">
                                                    <Link to={`/blog/artigo/${post.slug}`}>{post.title}</Link>
                                                </h3>

                                                {post.summary && <p className="manage-card-summary">{post.summary}</p>}

                                                {post.status === "rejected" && post.rejection_reason && (
                                                    <div className="manage-card-rejection">
                                                        <AlertCircle size={14} />
                                                        <span>{t("blog.manage.rejectionReason", { reason: post.rejection_reason })}</span>
                                                    </div>
                                                )}

                                                <div className="manage-card-actions">
                                                    <Link to={`/blog/artigo/${post.slug}`} className="manage-btn manage-btn-view">
                                                        <Eye size={14} />
                                                        <span>{t("blog.manage.view")}</span>
                                                    </Link>
                                                    <Link to={`/blog/editar/${post.id}`} className="manage-btn manage-btn-edit">
                                                        <Edit3 size={14} />
                                                        <span>{t("blog.manage.edit")}</span>
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(post.id)}
                                                        disabled={actionLoading}
                                                        className="manage-btn manage-btn-delete"
                                                    >
                                                        <Trash2 size={14} />
                                                        <span>{t("blog.manage.delete")}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "moderation" && canModerate && (
                        <>
                            <div className="moderation-header-banner">
                                <ShieldCheck size={20} />
                                <div>
                                    {t("blog.manage.moderationHeader")}
                                </div>
                            </div>

                            {loading ? (
                                <div className="blog-manage-loading">{t("blog.loading")}</div>
                            ) : pendingPosts.length === 0 ? (
                                <div className="blog-manage-empty">
                                    <CheckCircle size={48} className="empty-icon text-success" />
                                    <h3>{t("blog.manage.emptyModeration")}</h3>
                                    <p>{t("blog.manage.emptyModerationDesc")}</p>
                                </div>
                            ) : (
                                <div className="blog-manage-list">
                                    {pendingPosts.map((post) => (
                                        <div key={post.id} className="blog-manage-card moderation-card">
                                            {post.cover_image && (
                                                <div className="manage-card-img-wrapper">
                                                    <img src={coverSrc(post.cover_image!)} alt={post.title} className="manage-card-img" />
                                                </div>
                                            )}
                                            <div className="manage-card-body">
                                                <div className="manage-card-header">
                                                    {getStatusBadge(post.status)}
                                                    {post.user && (
                                                        <Link to={`/@${post.user.user_name}`} className="moderation-author-link">
                                                            <img
                                                                src={getImageUser(post.user.img_account, undefined, post.user.name)}
                                                                onError={(e) => { e.currentTarget.src = getImageUserNotFound(post.user?.name); }}
                                                                alt={post.user.name}
                                                                className="moderation-author-avatar"
                                                            />
                                                            <span>{post.user.name}</span>
                                                            <ImgVerified user={post.user} classNameProp="blog-verified-badge" />
                                                        </Link>
                                                    )}
                                                    <span className="manage-card-date">
                                                        <Calendar size={12} />
                                                        {new Date(post.created_at).toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                                    </span>
                                                </div>

                                                <h3 className="manage-card-title">
                                                    <Link to={`/blog/artigo/${post.slug}`}>{post.title}</Link>
                                                </h3>

                                                {post.summary && <p className="manage-card-summary">{post.summary}</p>}

                                                <div className="moderation-card-actions">
                                                    <Link to={`/blog/artigo/${post.slug}`} className="manage-btn manage-btn-view">
                                                        <Eye size={14} />
                                                        <span>{t("blog.readFullContent")}</span>
                                                    </Link>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleApprove(post.id)}
                                                        disabled={actionLoading}
                                                        className="manage-btn manage-btn-approve"
                                                    >
                                                        <CheckCircle size={14} />
                                                        <span>{t("blog.approveAndPublish")}</span>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleReject(post.id)}
                                                        disabled={actionLoading}
                                                        className="manage-btn manage-btn-reject"
                                                    >
                                                        <XCircle size={14} />
                                                        <span>{t("blog.refuse")}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default BlogManage;
